import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth-context';
import { useT } from '../i18n/locale-context';
import {
  saveCompanyName,
  submitCompany,
  type CompanyEntry,
  type CompanyPayoff,
} from '../lib/auth-client';
import { track } from '../lib/funnel';
import { countryConfig } from '../country';
import { CompanyPayoffLine } from './company-payoff';
import { companyErrorKey } from '../lib/company-error';
import {
  CompanyLookup,
  isNameLookup,
  tinFromLookup,
  typedNameFromLookup,
  type CompanyPick,
} from './company-lookup';

// UZ ИНН = 9 digits, KZ БИН/ИИН = 12 — the web only gates on length; the server
// does the real checksum. See `countryConfig.companyIdForm`.
const { digits: TIN_DIGITS } = countryConfig.companyIdForm;

/**
 * The post-signup company step (ADR 0008) — capture the ИНН where BOTH signup
 * methods land, with a payoff that justifies the ask.
 *
 * Shown to any signed-in user without an ИНН, which is deliberately the same
 * condition for a fresh signup and for the 612 existing accounts that never had
 * one. The ADR asks for a backfill campaign and a new-user step; they are the
 * same prompt, because they are the same missing field.
 *
 * A card in the page rather than a modal: the ИНН is valuable but not urgent,
 * and 23 paying customers already cannot use ИНН-gated features, so this has to
 * be re-encounterable rather than a one-shot interruption people reflexively
 * close.
 */
const shell = 'rounded-lg border border-edge bg-bone-surface p-4';

export function CompanyPrompt({
  onSkip,
  benefits = false,
  editing = false,
  bare = false,
  onResolved,
  onFinish,
  surface = 'card',
}: {
  /**
   * Render a «Позже» button that calls this.
   *
   * Two callers, two meanings: the reminder modal uses it to RETIRE the step,
   * settings uses it to cancel an edit and go back to the card. Neither makes
   * the permanent form dismissible — settings passes it only in `editing`
   * mode, where there is already an ИНН to return to.
   */
  onSkip?: () => void;
  /** Show the "why we ask" list. Off in settings, where the user arrived
   *  deliberately; on in the modal, which interrupts them. */
  benefits?: boolean;
  /** Render even when an ИНН is already stored — the CORRECTION path.
   *
   *  Without this the component early-returns on `user.tin`, which is precisely
   *  why a mistyped ИНН used to be permanent: the API would have accepted an
   *  overwrite all along, but nothing could reach it. The caller opts in
   *  explicitly so the default (hide once satisfied) is unchanged everywhere
   *  else. */
  editing?: boolean;
  /** Drop the card chrome (border, surface, padding).
   *
   *  Inside a dialog the surface is already provided, and rendering our own on
   *  top produced a visible card-within-a-card — a beige frame around a white
   *  one. The chrome belongs to whichever container owns the background, and in
   *  a modal that is the modal. */
  bare?: boolean;
  /** Called with the outcome as soon as a submission resolves, so a container
   *  can react — the modal retitles itself from an ask to a confirmation. */
  onResolved?: () => void;
  /** Renders «Готово» on the RESULT screen. Without it the payoff is a dead
   *  end: the form is gone, so the only way out is the overlay. */
  onFinish?: () => void;
  /** Where the ask is being rendered — `card` (settings/checklist) or `modal`
   *  (the interrupting reminder/onboarding dialog). Rides on the
   *  `company_prompt_shown` event so its conversion can be split by surface. */
  surface?: string;
} = {}) {
  const { user, status, refresh } = useAuth();
  const t = useT();
  // What the «Название или ИНН» field holds — a name or digits. Only digits are
  // an ИНН (`tinFromLookup`); a name reaches the API only as a PICKED
  // suggestion, whose ИНН «Продолжить» then submits.
  const [text, setText] = useState('');
  const [picked, setPicked] = useState<CompanyPick | null>(null);
  const tin = tinFromLookup(text);
  // Spain (`keepsTypedName`): a name nobody's register holds is saved as typed —
  // alone, or beside a full NIF. Always null on UZ/KZ, so `ready` is unchanged.
  const typedName = picked ? null : typedNameFromLookup(text);
  const ready = picked !== null || tin.length === TIN_DIGITS || typedName !== null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payoff, setPayoff] = useState<CompanyPayoff | null>(null);

  // The denominator for the claim funnel: we asked an un-claimed user for their
  // ИНН. Fires once when the ask form is shown — not on the payoff result
  // screen, and not on the `editing` correction path (that user already has an
  // ИНН). Kept above the early returns so the hook order never changes.
  const askShown = status === 'authed' && !!user && !user.tin && !payoff;
  useEffect(() => {
    if (askShown) track('company_prompt_shown', { surface });
  }, [askShown, surface]);

  // Only for signed-in users who have no ИНН yet — EXCEPT once we have a payoff
  // to show, which outranks every reason to hide.
  //
  // ⚠️ `!payoff` is load-bearing on the `user.tin` clause.
  // `handleSubmit` awaits `refresh()` on success, whose entire job is to
  // put the new ИНН on the session — so the moment the step SUCCEEDS, `user.tin`
  // becomes truthy and this line was unmounting the component before the result
  // below could render. The card simply vanished, and the payoff that justifies
  // asking for a government identifier at all ("you have already won 18,656
  // tenders") was fetched, parsed and discarded without ever reaching a pixel.
  //
  // The unit tests did not catch it because they mock `refresh` as a no-op, so
  // `user.tin` never changed and the guard never fired. Two tests now make the
  // mock do what the real one does.
  // ⚠️ NO dismissal, and `innSkipped` is deliberately NOT consulted.
  //
  // This card is the ИНН's permanent home in settings; the banner and the modal
  // are reminders ABOUT it. Dismissing a reminder must not remove the thing it
  // reminds you of — a user who says «Мне это не нужно» to stop being nagged,
  // and later wants to add their ИНН, has to find it somewhere. Here.
  //
  // It earlier had its own permanent localStorage flag, so one «Позже» deleted
  // the form for good while the checklist carried on pointing at it.
  if (status !== 'authed' || !user) return null;
  if (user.tin && !payoff && !editing) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (picked) {
      await submit(picked.company.tin, picked.via);
      return;
    }
    // A full ИНН — typed alone or beside a name — is saved as is, whether or
    // not anyone has heard of the company (owner, 2026-10-18: «может это
    // новая компания»). The server stores it before it looks anything up.
    if (tin.length === TIN_DIGITS) {
      await submit(tin, 'tin', typedName);
      return;
    }
    // Spain: no id, a name — kept as typed (owner, 2026-10-21).
    if (typedName) {
      await saveName(typedName);
      return;
    }
    // A NAME in the field with nothing picked: Enter / «Go» has nothing to
    // send — the answer is a tap on a suggestion, and «нужно 9 цифр» would
    // read as if typing the name had been wrong.
    if (isNameLookup(text)) return;
    // The button is disabled until the length is right, but Enter / the phone
    // keyboard's «Go» still submits the form — say what is wrong instead of
    // doing nothing, and never send a number the API is bound to refuse.
    setError(t('company.error.invalidTin', { digits: TIN_DIGITS }));
  }

  async function saveName(name: string) {
    setError(null);
    setBusy(true);
    try {
      setPayoff(await saveCompanyName(name));
      onResolved?.();
      await refresh();
    } catch (err) {
      setError(t(companyErrorKey(err), { digits: TIN_DIGITS }));
    } finally {
      setBusy(false);
    }
  }

  async function submit(value: string, via: CompanyEntry, name?: string | null) {
    setError(null);
    setBusy(true);
    try {
      // The typed name rides along only when there is one (Spain), so every
      // other submission is the exact call it always was.
      const result = name ? await submitCompany(value, via, name) : await submitCompany(value, via);
      setPayoff(result);
      onResolved?.();
      // The ИНН is now on the user, so `user.tin` becomes truthy and this card
      // stops rendering on the next mount. Refresh so the rest of the app sees
      // it immediately (ИНН-gated features, checkout).
      await refresh();
    } catch (err) {
      // With the id sent: a full-length NIF refused is a wrong control letter.
      setError(t(companyErrorKey(err, value), { digits: TIN_DIGITS }));
    } finally {
      setBusy(false);
    }
  }

  // The result screen. `none` is deliberately absent from this switch: when we
  // have nothing true to say, saying nothing beats "we found 0 about you".
  if (payoff) {
    const finish = onFinish && (
      <button
        type="button"
        onClick={onFinish}
        className="mt-4 inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-4 text-[14px] font-bold text-white transition-colors hover:bg-brand-mid"
      >
        {t('company.done')}
      </button>
    );

    // `none` says nothing about counts — silence beats "we found 0 about you".
    // In a modal it says nothing at all: the dialog's heading already reads
    // «ИНН сохранён», and repeating it as body text is the same sentence twice.
    // Spain's `none` with a typed name says the name back instead (below).
    if (payoff.kind === 'none' && !payoff.typedName) {
      return (
        <div className={bare ? '' : shell}>
          {!bare && <p className="text-[14px] text-ink">{t('company.saved')}</p>}
          {finish}
        </div>
      );
    }
    return (
      <div className={bare ? '' : 'rounded-lg border border-brand/30 bg-brand/5 p-4'}>
        <p className="text-[15px] font-semibold text-ink">
          <CompanyPayoffLine payoff={payoff} />
        </p>
        {payoff.companyName && (
          <p className="mt-1 text-[13px] text-ink-soft">{payoff.companyName}</p>
        )}
        {payoff.supplierLinkConflict && (
          <p className="mt-1 text-[12.5px] text-status-closing-text" role="alert">
            {t('claim.conflict.title')}
          </p>
        )}
        {finish}
      </div>
    );
  }

  return (
    <div className={bare ? '' : shell}>
      {/* Suppressed in `bare` mode — the dialog renders the heading itself, and
          two copies duplicated it in the accessibility tree. */}
      {!bare && <p className="text-[16px] font-bold text-ink">{t('company.title')}</p>}
      {/* The bullets below carry the reasons when they are shown, and the first
          of them repeated this sentence almost word for word. One statement of
          a benefit, not two. */}
      {!benefits && <p className="mt-1 text-[13px] text-ink-soft">{t('company.subtitle')}</p>}

      {/* Every line here is a claim the code actually delivers — audited
          2026-08-07 against the real endpoint. Notably ABSENT: "we'll set up
          alerts for your industry", which the old subtitle promised. Automatic
          alerts only exist for a TIN with at least one award (the link to a
          supplier profile happens in that branch alone, and ~70% of TIN holders
          have no awards), and they are derived from what the company actually
          WON, not from its registry sector. Also absent: "you'll need it for
          invoices" — nothing prefills an invoice with it. Do not restore either
          without making them true first. */}
      {benefits && (
        <ul className="mt-4 flex flex-col gap-2 text-[13px] leading-relaxed text-ink-soft">
          <li className="flex gap-2">
            <span aria-hidden className="text-brand">
              •
            </span>
            {t('company.benefit.wins')}
          </li>
          <li className="flex gap-2">
            <span aria-hidden className="text-brand">
              •
            </span>
            {t('company.benefit.alerts')}
          </li>
          <li className="flex gap-2">
            <span aria-hidden className="text-brand">
              •
            </span>
            {t('company.benefit.plan')}
          </li>
        </ul>
      )}

      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        className="mt-4 flex flex-col gap-2"
      >
        {/* One field for a name OR the ИНН (owner, 2026-10-18): a tapped
            suggestion is selected, digits are the old path; «Продолжить»
            submits whichever it holds. */}
        <CompanyLookup
          value={text}
          onChange={(next) => {
            setText(next);
            setPicked(null);
            setError(null);
          }}
          picked={picked}
          onPick={(pick) => {
            setText(pick.company.name);
            setPicked(pick);
            setError(null);
          }}
          canSkip={Boolean(onSkip)}
          disabled={busy}
        />
        {/* Primary action last and full-width: the eye ends on the commit, and
            on a phone it is a thumb-width target rather than a 160px chip
            sharing a row with the input. */}
        <button
          type="submit"
          disabled={busy || !ready}
          className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-4 text-[14px] font-bold text-white transition-colors hover:bg-brand-mid disabled:pointer-events-none disabled:opacity-60"
        >
          {busy ? t('company.checking') : t('company.submit')}
        </button>
        {onSkip && (
          // Deliberately quiet and BELOW the commit — a real answer, but not a
          // peer of the action this screen exists to get.
          <button
            type="button"
            onClick={onSkip}
            className="inline-flex h-9 w-full items-center justify-center rounded-md text-[13px] font-semibold text-ink-muted transition-colors hover:text-ink"
          >
            {t('company.later')}
          </button>
        )}
      </form>

      {error && (
        <p className="mt-2 text-[13px] text-status-closing-text" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
